import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const ImageAnalytics = ({ girlId, isOwner = false }) => {
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    totalLikes: 0,
    popularImages: [],
    dailyViews: [],
    monthlyViews: [],
    revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d, 1y

  // ดึงข้อมูล Analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      if (!girlId) return;

      // ดึงข้อมูลน้อง
      const girlRef = doc(db, 'girls', girlId);
      const girlSnap = await getDoc(girlRef);
      
      if (!girlSnap.exists()) return;

      const girlData = girlSnap.data();
      const images = girlData.images || [];
      
      // คำนวณสถิติพื้นฐาน
      const totalViews = images.reduce((sum, img) => sum + (img.views || 0), 0);
      const totalLikes = images.reduce((sum, img) => sum + (img.likes || 0), 0);
      
      // รูปที่ได้รับความนิยม
      const popularImages = images
        .map((img, index) => ({ ...img, index }))
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 5);

      // คำนวณรายได้ (ตัวอย่าง: 1 view = 1 T Coin)
      const revenue = totalViews * 1;

      // สร้างข้อมูล daily views (ตัวอย่าง)
      const dailyViews = generateDailyViewsData(timeRange, images);

      setAnalytics({
        totalViews,
        totalLikes,
        popularImages,
        dailyViews,
        monthlyViews: [], // TODO: คำนวณข้อมูลรายเดือน
        revenue
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // สร้างข้อมูล daily views (ตัวอย่าง)
  const generateDailyViewsData = (range, images) => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 365;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // ตัวอย่าง: สุ่มจำนวนการดู
      const views = Math.floor(Math.random() * 20) + 5;
      data.push({
        date: dateStr,
        views,
        formattedDate: date.toLocaleDateString('th-TH', { 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    
    return data;
  };

  useEffect(() => {
    fetchAnalytics();
  }, [girlId, timeRange]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">สถิติรูปภาพ</h3>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm"
        >
          <option value="7d">7 วันที่ผ่านมา</option>
          <option value="30d">30 วันที่ผ่านมา</option>
          <option value="90d">90 วันที่ผ่านมา</option>
          <option value="1y">1 ปีที่ผ่านมา</option>
        </select>
      </div>

      {/* สถิติหลัก */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">👁️</span>
            <div>
              <div className="text-2xl font-bold text-blue-600">{analytics.totalViews}</div>
              <div className="text-sm text-blue-700">การดูทั้งหมด</div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">❤️</span>
            <div>
              <div className="text-2xl font-bold text-green-600">{analytics.totalLikes}</div>
              <div className="text-sm text-green-700">ไลค์ทั้งหมด</div>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">💰</span>
            <div>
              <div className="text-2xl font-bold text-purple-600">{analytics.revenue}</div>
              <div className="text-sm text-purple-700">T Coins</div>
            </div>
          </div>
        </div>
      </div>

      {/* กราฟการดูรูป */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h4 className="font-semibold mb-4">การดูรูปภาพรายวัน</h4>
        <div className="h-64 flex items-end space-x-1">
          {analytics.dailyViews.map((day, index) => {
            const maxViews = Math.max(...analytics.dailyViews.map(d => d.views));
            const height = (day.views / maxViews) * 100;
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="bg-blue-500 w-full rounded-t transition-all duration-300 hover:bg-blue-600"
                  style={{ height: `${height}%` }}
                  title={`${day.formattedDate}: ${day.views} ครั้ง`}
                ></div>
                <div className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-left">
                  {day.formattedDate}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* รูปที่ได้รับความนิยม */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h4 className="font-semibold mb-4">รูปที่ได้รับความนิยม</h4>
        <div className="space-y-3">
          {analytics.popularImages.map((image, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={image.url}
                  alt={`Popular ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRkZCNkMxIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiMzMzMiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlIEVycm9yPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {image.originalName || `รูปที่ ${index + 1}`}
                </div>
                <div className="text-xs text-gray-500">
                  อัพโหลดเมื่อ: {image.uploadedAt ? new Date(image.uploadedAt).toLocaleDateString('th-TH') : 'ไม่ระบุ'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">{image.views || 0}</div>
                <div className="text-xs text-gray-500">ครั้ง</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ข้อมูลเพิ่มเติม */}
      {isOwner && (
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-semibold text-yellow-800 mb-2">💡 เคล็ดลับเพิ่มการดูรูป</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• อัพโหลดรูปคุณภาพสูงและสวยงาม</li>
            <li>• ใช้ชื่อไฟล์ที่อธิบายรูปได้ชัดเจน</li>
            <li>• อัพโหลดรูปใหม่เป็นประจำ</li>
            <li>• ใช้รูปที่แสดงบุคลิกภาพและความสวยงาม</li>
            <li>• อัพเกรด Tier เพื่ออัพโหลดรูปได้มากขึ้น</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ImageAnalytics;









